import {
    IMyAccountExternalLinkHandler,
    IOpenMyAccountOptions
} from "./my-account.external-link-handler.interface";
import {MyAccountTab} from "@sparkware/uc-sdk-core";
import {CHANNEL_ELEMENTS, CHANNEL_SOURCE} from "../../../../../common/const/casinofied-analytics-consts";
import {AreaType} from "@sparkware/uc-sdk-core";
import {NullableUndefinedNumber, NullableUndefinedString} from "../../../../../common/types/nullable-types";
import {OpenRichPageOptions} from "../../external-links.service.interface";
import {CIPropRead} from "../../../../../utils/case-insensitive-property-reader";
import {ExternalLinksZIndexes} from "../../external-links-z-indexes";
import {
    ExternalLinkInIframeHandlerBase,
    ExternalLinkInIframeRendererParams
} from "../external-link-in-iframe-handler-base";
import {GNWP_SOURCES} from "../../../gnwp/gnwp-consts";
import {CyClass} from "../../../../../common/cy-classes";

const MY_ACCOUNT_POST_ACTIONS = {
    GAME: '1',
    CASHIER: '6',
    QUICK_DEPOSIT: '7',
    CASINO: '22',
    SPORT: '24',
    EXTERNAL_URL: '27',
    CLOSE: 'CloseMe',
    RICH: '56',
    INTERNAL_URL: '32'
}


export class MyAccountExternalLinkHandler extends ExternalLinkInIframeHandlerBase implements IMyAccountExternalLinkHandler {

    protected _getGnwpSource(): GNWP_SOURCES {
        return GNWP_SOURCES.MY_ACCOUNT;
    }

    async openMyAccount(options?: IOpenMyAccountOptions): Promise<void> {

        if(!await this.services.user.ensureUserIsLoggedIn('MyAccountExternalLinkHandler')) {
            return;
        }

        if(options?.tabId === MyAccountTab.UploadDocuments) {
            await this.services.externalLinks.openUploadDocumentFromMyAccount();
            return;
        }

        const url = await this._getGnwpUrl(options);
        await this._showExternalLinkDialog(url);

    }


    protected async _getGnwpUrl(options?: IOpenMyAccountOptions): Promise<string> {
        return await this.services.gnwp.getMyAccountUrl({
            tabId: options?.tabId,
        });
    }

    protected _getIframeRendererParams(): ExternalLinkInIframeRendererParams {
        return {
            maxWidth: 940,
            maxHeight: 658,
            zIndex: ExternalLinksZIndexes.MyAccount,
            hideCloseButton: false,
            httpVerb: 'POST',
            analyticsAction: this._getGnwpSource(),
            iframeExtraCssClass: CyClass('cy-my-account-external-link-container')
        };
    }

    protected async _onPostMessageReceived(message:MessageEvent): Promise<void> {
        const actionId = this._extractActionId(message);
        if(!actionId) {
            return;
        }
        switch (actionId) {
            case MY_ACCOUNT_POST_ACTIONS.QUICK_DEPOSIT:
                await this._openQuickDeposit({
                    promCode: this._extractPromoCode(message),
                    channel: {
                        source: CHANNEL_SOURCE.myAccount,
                        area: AreaType.Header,
                        element: CHANNEL_ELEMENTS.depositButton
                    }
                });
                break;
            case MY_ACCOUNT_POST_ACTIONS.CASHIER:
                await this._openCashier({
                    tabId: this._extractCashierTabId(message),
                    promCode: this._extractPromoCode(message),
                    channel: {
                        source: CHANNEL_SOURCE.myAccount,
                        area: AreaType.Header
                    }
                });
                break;
            case MY_ACCOUNT_POST_ACTIONS.GAME:
                await this._openGame( {
                    gameType: this._extractGameId(message),
                    originDeepLinkID: CIPropRead.getNumericProperty('originDeepLinkID',
                        CIPropRead.getProperty('origininformation', this._parseMessageData(message)))
                });
                break;
            case MY_ACCOUNT_POST_ACTIONS.RICH:
                await this._openRichPage({
                    campaignId: CIPropRead.getStringProperty('campaignID', this._parseMessageData(message))
                });
                break;
            case MY_ACCOUNT_POST_ACTIONS.SPORT:
                await this._openSport();
                break;
            case MY_ACCOUNT_POST_ACTIONS.CASINO:
            case MY_ACCOUNT_POST_ACTIONS.CLOSE:
                this._closeExternalLinkDialog();
                break;
            case MY_ACCOUNT_POST_ACTIONS.INTERNAL_URL:
                const internalUrl = this._extractUrl(message);
                if(internalUrl) {
                    await this._openInternalUrl(internalUrl)
                } else {
                    this.services.logger.error('My Account redirect to internal url failed because no URL provided');
                }

                break;
            case MY_ACCOUNT_POST_ACTIONS.EXTERNAL_URL:
                const externalUrl = this._extractUrl(message)
                if(externalUrl) {
                    await this._openExternalUrl(externalUrl);
                } else {
                    this.services.logger.error('My Account redirect to external url failed because no URL provided');
                }
                break;
            default:
                this.services.logger.debug(`Received unknown actionId ${actionId} from My account`);

        }
    }

    protected async _openGame(options: {gameType: NullableUndefinedNumber, originDeepLinkID: NullableUndefinedNumber}): Promise<void> {
        this._closeExternalLinkDialog();
        if(!options.gameType) {
            this.services.logger.debug('My account sent open game event with undefined game type');
            return;
        }

        const gameInfo = await this.services.games.getGameInfo(options.gameType);

        if(!gameInfo) {
            this.services.logger.debug(`Can't open game ${options.gameType} from My Account because is not in the layout`);
            return;
        }

        await gameInfo.open({
            isRealMode: true,
            referrerId: options.originDeepLinkID,
        });
    }

    protected async _openRichPage(options: OpenRichPageOptions): Promise<void> {
        this._closeExternalLinkDialog();
        await this.services.externalLinks.openRichPage(options);
    }

    protected async _openSport(): Promise<void> {
        this._closeExternalLinkDialog();
        await this.services.linksNavigation.openSport();
    }

    protected async _openInternalUrl(internalUrl: NullableUndefinedString): Promise<void> {
        this._closeExternalLinkDialog();
        if(internalUrl) {
            await this.services.linksNavigation.openUrl({
                url: internalUrl,
                openMode: "clientSideNavigation"
            });
        } else {
            this.services.logger.error('My account asked to open internal url but no url was provided in the post message');
        }
    }

    protected async _openExternalUrl(externalUrl: NullableUndefinedString): Promise<void> {
        this._closeExternalLinkDialog();
        if(externalUrl) {
            await this.services.linksNavigation.openUrl({
                url: externalUrl,
                openMode: "newTab"
            });
        } else {
            this.services.logger.error('My account asked to open external url but no url was provided in the post message');
        }

    }

}
